import { useDroppable } from '@dnd-kit/core';
import { UserPlus } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import { cn } from '@/lib/utils';

export interface DrawEmptySlotParticipantOption {
  id: string;
  displayName: string;
}

interface PlayEventDrawEmptySlotProps {
  matchKey: string;
  side: 'a' | 'b';
  slotIndex?: number;
  editable: boolean;
  label?: string;
  draggingParticipantId?: string | null;
  availableParticipants?: DrawEmptySlotParticipantOption[];
  onAssignParticipant?: (participantId: string) => void;
}

export function PlayEventDrawEmptySlot({
  matchKey,
  side,
  slotIndex = 0,
  editable,
  label = 'TBD',
  draggingParticipantId = null,
  availableParticipants = [],
  onAssignParticipant,
}: PlayEventDrawEmptySlotProps) {
  const dropId = `drop-empty-${matchKey}-${side}-slot${slotIndex}`;
  const { setNodeRef, isOver } = useDroppable({
    id: dropId,
    disabled: !editable,
    data: { matchKey, side, empty: true },
  });

  const isDropTarget = Boolean(isOver && draggingParticipantId);
  const showPicker =
    editable && availableParticipants.length > 0 && onAssignParticipant != null;

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex min-h-[2.75rem] flex-col gap-2 rounded-md border border-dashed px-2 py-2 text-sm transition-colors',
        editable
          ? 'border-muted-foreground/30 text-muted-foreground'
          : 'border-transparent text-muted-foreground/70',
        isDropTarget && 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-500/10'
      )}
    >
      <div className="flex items-center gap-2">
        <UserPlus className="h-4 w-4 shrink-0 opacity-60" aria-hidden />
        <span>{isDropTarget ? 'Drop player here' : label}</span>
      </div>
      {showPicker ? (
        <Select
          onValueChange={(value) => {
            onAssignParticipant(value);
          }}
        >
          <SelectTrigger
            className="h-8 w-full bg-background text-foreground"
            aria-label={`Add player to side ${side.toUpperCase()}`}
          >
            <SelectValue placeholder="Add player" />
          </SelectTrigger>
          <SelectContent>
            {availableParticipants.map((participant) => (
              <SelectItem key={participant.id} value={participant.id}>
                {participant.displayName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}
    </div>
  );
}

export function parseEmptySlotDropId(
  overId: string | number
): { matchKey: string; side: 'a' | 'b'; slotIndex: number } | null {
  const id = String(overId);
  if (!id.startsWith('drop-empty-')) return null;

  const slotted = id.match(/^drop-empty-(.*)-(a|b)-slot(\d+)$/);
  if (slotted) {
    return {
      matchKey: slotted[1],
      side: slotted[2] as 'a' | 'b',
      slotIndex: Number(slotted[3]),
    };
  }

  const remainder = id.slice('drop-empty-'.length);
  const lastDash = remainder.lastIndexOf('-');
  if (lastDash < 0) return null;
  const matchKey = remainder.slice(0, lastDash);
  const side = remainder.slice(lastDash + 1);
  if (side !== 'a' && side !== 'b') return null;
  return { matchKey, side, slotIndex: 0 };
}
